import { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import Box from '@material-ui/core/Box';
import { useRouter } from 'next/router';
import Alert from '@material-ui/lab/Alert';
import TextField from '@material-ui/core/TextField';
import { Controller, useForm } from 'react-hook-form';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import InputAdornment from '@material-ui/core/InputAdornment';

import { useFlagsmith } from 'flagsmith/react';
import { useEventsTracker } from 'hooks/events-tracker/useEventsTracker';
import { useNameComponent } from 'hooks';
import { urls } from '../../../libs/urls';
import BussinessInfo from './BussinessInfo';
import ReferralCodeForm from './ReferralCodeForm';
import { FlagAvatar } from '../common/FlagAvatar';
import { useAuthStore } from '../../infra/store/useAuthStore';
import { Link, LoadingButton, Spacer } from '../../../shared';
import countries from '../../../../public/locales/countries.json';
import { CountryFlagSelector } from '../common/CountryFlagSelector';
import { useTranslation } from '../../../i18n/infra/useTranslation';
import useUnifiedRegister from '../../adapters/Register/useUnifiedRegister';
import { UnifiedRegisterData } from '../../domain/entity/UnifiedRegisterData';
import { useAuthRepository } from '../../infra/repositories/useAuthRepository';
import { useEmailRules } from '../../../../src/helpers/validations/rules/useEmailRules';
import { usePasswordRules } from '../../../../src/helpers/validations/rules/usePasswordRules';
import { usePhoneRules } from '../../../../src/helpers/validations/rules/usePhoneRules';
import useRedirectWhenFullyRegistered from './hooks/useRedirectWhenFullyRegistered';
import { TermsAndConditions } from '../common';

import { useDefaultRegistrationValues } from './utils';
import { DetailError } from '../../domain/AuthStore';

interface IRegisterForm {
  dataCy?: string;
}

const UnifiedRegister = function UnifiedRegister({ dataCy }: IRegisterForm) {
  const router = useRouter();
  const { t } = useTranslation();
  const authStore = useAuthStore();
  const featureFlagsManager = useFlagsmith();
  const [showPassword, setShowPassword] = useState(false);
  const { countryId } = authStore;

  const eventsTracker = useEventsTracker();
  const authRepository = useAuthRepository();

  // Get the default registration values from the path's query parameters to prefill form inputs.
  const {
    defaultEmail,
    defaultFullname,
    defaultPhonenumber,
    defaultCompany,
    defaultIdentifier,
    defaultReferralCode,
  } = useDefaultRegistrationValues(router, countryId);

  const hookForm = useForm<UnifiedRegisterData>({
    mode: 'onChange',
  });
  const { control, handleSubmit, setError, setValue, clearErrors } = hookForm;

  const setDetailError = (details: DetailError[]) => {
    clearErrors();
    details.forEach((error: any) => {
      setError(error.key, {
        message: t(error.errorCode),
      });
      return error;
    });
  };

  const { onSubmit, isLoading, isAuthError } = useUnifiedRegister({
    authStore,
    authRepository,
    eventsTracker,
    featureFlagsManager,
    setDetailError,
  });

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  const emailRules = useEmailRules();
  const passwordRules = usePasswordRules();
  const nameRules = {
    required: t('REQUIRED_NAME'),
    minLength: {
      value: 3,
      message: t('ERROR_MIN_LENGTH_NAME'),
    },
    maxLength: {
      value: 255,
      message: t('ERROR_MAX_LENGTH_NAME'),
    },
  };
  const phoneRules = usePhoneRules();

  useEffect(() => {
    if (eventsTracker) {
      setTimeout(() => {
        eventsTracker.sendEvent('NEW_REGISTER_ON_LOAD', {
          source: 'UNIFIED_REGISTER',
        });
      }, 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useRedirectWhenFullyRegistered(authStore);

  // When the router is ready we prefill the inputs with registration the default registration values.
  // We are not using either useForm, Controller or TextField's defaultValue property because they are only set once and
  // router.query is empty on first render, making them always empty. We need to wait for router.isReady and change the
  // values dynamically.
  //
  // (see: https://stackoverflow.com/questions/61040790/userouter-withrouter-receive-undefined-on-query-in-first-render)
  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    // Change input values
    setValue('email', defaultEmail);
    setValue('name', defaultFullname);
    setValue('phone', defaultPhonenumber);

    // Only in MX we set the company name
    if (countryId === 'MX') {
      setValue('businessName', defaultCompany);
    }

    setValue('referralCode', defaultReferralCode);
    setValue('identifier', defaultIdentifier);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady]);

  return (
    <Box maxWidth="420px" textAlign="center" width="100%">
      <CountryFlagSelector dataCy={useNameComponent(dataCy, 'country')} />
      <Spacer spacing={2} />
      <Typography component="div" variant="h4">
        <Box fontWeight="fontWeightMedium">Regístrate en Xepelin</Box>
      </Typography>
      <Spacer spacing={4} />
      <Box width="100%">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="name"
            rules={nameRules}
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                variant="outlined"
                placeholder={t('PLACEHOLDER_NAME')}
                label={t('LABEL_NAME')}
                value={field.value || ''}
                type="text"
                fullWidth
                disabled={isLoading}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                autoFocus={!isMobile}
                InputProps={{
                  inputProps: {
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    'data-cy': useNameComponent(dataCy, 'name'),
                  },
                }}
              />
            )}
          />
          <Spacer spacing={2} />
          <Controller
            name="phone"
            rules={phoneRules}
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                variant="outlined"
                placeholder={t('PLACEHOLDER_PHONE')}
                label={t('LABEL_PHONE')}
                value={field.value || ''}
                type="tel"
                fullWidth
                disabled={isLoading}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                InputProps={{
                  inputProps: {
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    'data-cy': useNameComponent(dataCy, 'phone'),
                  },
                  startAdornment: (
                    <InputAdornment position="start">
                      <FlagAvatar height="23px" width="23px" />
                      &nbsp;
                      <p>{`(${countries[countryId].code})`}</p>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
          <Spacer spacing={2} />
          <Controller
            name="email"
            rules={emailRules}
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                variant="outlined"
                placeholder={t('PLACEHOLDER_EMAIL')}
                label={t('LABEL_EMAIL')}
                value={field.value || ''}
                type="text"
                fullWidth
                error={!!fieldState.error}
                disabled={isLoading}
                helperText={
                  fieldState.error?.message ||
                  (fieldState.error?.type === 'validate'
                    ? t('ERROR_INVALID_EMAIL')
                    : '')
                }
                InputProps={{
                  inputProps: {
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    'data-cy': useNameComponent(dataCy, 'email'),
                  },
                }}
              />
            )}
          />
          <Spacer spacing={2} />
          <Controller
            name="password"
            control={control}
            rules={passwordRules}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                variant="outlined"
                name="password"
                autoComplete="new-password"
                type={showPassword ? 'text' : 'password'}
                label={t('LABEL_PASSWORD')}
                value={field.value || ''}
                placeholder={t('PLACEHOLDER_PASSWORD')}
                disabled={isLoading}
                error={!!fieldState.error}
                helperText={
                  fieldState.error?.message ||
                  (fieldState.error?.type === 'validate'
                    ? t('ERROR_INVALID_PASSWORD')
                    : '')
                }
                InputProps={{
                  inputProps: {
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    'data-cy': useNameComponent(dataCy, 'password'),
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={t('HELPER_PASSWORD_VISIBILITY_ICON')}
                        edge="end"
                        onClick={handleClickShowPassword}
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
          <Spacer spacing={2} />
          <BussinessInfo
            countryId={countryId}
            hookForm={hookForm}
            dataCy={useNameComponent(dataCy)}
          />
          <Spacer spacing={2} />
          <ReferralCodeForm
            hookForm={hookForm}
            isUnifiedView
            dataCy={useNameComponent(dataCy)}
          />
          {isAuthError ? (
            <>
              <Spacer spacing={2} />
              <Alert severity="error">
                <Typography variant="subtitle2">
                  {t(authStore.authError)}
                </Typography>
              </Alert>
            </>
          ) : null}
          <Spacer spacing={2} />
          <LoadingButton
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            isLoading={isLoading}
            dataCy={useNameComponent(dataCy, 'button')}
          >
            Registrarme
          </LoadingButton>
        </form>
        <Spacer spacing={2} />
        <Typography variant="body2">
          ¿Ya tienes una cuenta?{' '}
          <Link href={{ pathname: urls.login, query: router.query }}>
            Inicia sesión aquí
          </Link>
        </Typography>
        <Spacer spacing={4} />
        <TermsAndConditions countryId={countryId} />
      </Box>
    </Box>
  );
};

export default UnifiedRegister;
